import React, { useEffect, useMemo, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Tooltip,
  Avatar,
  Stack,
  Pagination,
  CircularProgress,
} from '@mui/material';
import EmptyState from '@shared/ui/common/EmptyState';

import { Warning } from '@mui/icons-material';
import { Product } from '@domain/product/models/Product';
import { useSales } from '../hooks/useSales';



import { useProductsFilter } from '@features/products/hooks/useProductsFilter';
import { getISOWeek, getISOWeekYear, setISODay, setISOWeek, setISOWeekYear } from 'date-fns';
import ForecastGrade from '@features/timetravel/components/ForecastAnalytics/ForecastGrade';

interface SalesTableProps {
  onProductSelect?: (selectedProduct: Product) => void;
  selectedProduct?: Product;
  startDate: Date;
  endDate: Date;
  marketplaces?: string[];
  countries?: string[];
}

const SalesTable: React.FC<SalesTableProps> = ({
  onProductSelect,
  selectedProduct,
  startDate,
  endDate,
  marketplaces,
  countries,
}) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const hasAutoScrolledRef = useRef(false);

  const { filterCriteria, page, setPage, limit } = useProductsFilter(50);

  const filterKey = useMemo(() => JSON.stringify({ filterCriteria, marketplaces, countries }), [filterCriteria, marketplaces, countries]);

  const { sales, isLoading } = useSales({
    startDate: startDate,
    endDate: endDate,
    filterCriteria: { ...filterCriteria, page, limit, active: true, purchasable: true },
    marketplaces,
    countries,
  });

  const weeks = useMemo(() => {
    if (sales?.status !== 'success') return [];

    const weeks = new Map<string, { year: number, week: number }>();

    sales?.data?.forEach((productSales) => {
      productSales.weeks.forEach((week) => {

        if (!weeks.has(`${week.year}-${week.week}`)) {
          weeks.set(`${week.year}-${week.week}`, { year: week.year, week: week.week });
        }
      });
    });

    return Array.from(weeks.values()).sort((a, b) => a.year - b.year || a.week - b.week);
  }, [sales]);

  useEffect(() => {
    hasAutoScrolledRef.current = false;
  }, [page, filterKey]);


  const handleProductClick = (product: Product) => {
    onProductSelect?.(product);
  };


  const getDateFromYearAndWeek = (year: number, week: number): { startDate: Date, endDate: Date } => {
    // Start from January 4th, which is always in the first ISO week of the year
    let date = new Date(year, 0, 4);
    // Set the ISO week and year
    date = setISOWeekYear(date, year);
    date = setISOWeek(date, week);
    // Ensure it's Monday
    startDate = setISODay(date, 1);
    endDate = setISODay(date, 7);

    return { startDate, endDate };
  }


  return (
    sales?.status === 'success' && sales?.data?.length === 0 ? (
      <EmptyState
        title="No Sales Data Available"
        description="There is no sales data to display for the selected period."
      />
    ) : (
      <Stack>
        <TableContainer ref={tableContainerRef}>
          <Table stickyHeader>
            <TableHead >
              <TableRow >
                <TableCell
                  sx={{
                    width: '250px',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'background.paper',
                    zIndex: 3,
                    borderRight: '1px solid #e0e0e0',
                  }}
                >
                  Product
                </TableCell>
                {weeks.map((week, index) => (
                  <TableCell
                    key={`${week.year}-${week.week}`}
                    data-week-index={index}
                    align="center"
                    sx={{
                      width: '90px',
                      minWidth: 90,
                      '& .week-label': {
                        opacity: 0.7,
                        fontSize: '0.75rem',
                        marginBottom: 0.5,
                      },
                    }}
                  >
                    <Typography className="week-label">Week </Typography>
                    <Typography variant="subtitle2">{week.year} - {week.week} </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sales?.status === 'success' && sales?.data?.map(productData => (
                <TableRow
                  key={productData.product.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    backgroundColor: selectedProduct?.id === productData.product.id
                      ? 'action.selected'
                      : 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  <TableCell
                    onClick={() => handleProductClick(productData.product)}
                    sx={{
                      position: 'sticky',
                      left: 0,
                      backgroundColor: 'background.paper',
                      zIndex: 3,
                      borderRight: '1px solid #e0e0e0',
                      cursor: 'pointer',
                    }}
                  >
                    <Tooltip title={`EAN: ${productData.product.id}`} placement="right">
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          src={productData.product.image}
                          variant="rounded"
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: 'grey.100',
                          }}
                        >
                          {productData.product.title?.charAt(0) || '?'}
                        </Avatar>
                        <Typography variant="body2" sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {productData.product.title || productData.product.id}
                        </Typography>

                        <ForecastGrade grade={productData.forecastGrade} />
                      </Stack>
                    </Tooltip>
                  </TableCell>
                  {weeks.map(week => {
                    const weekData = productData.weeks.find(w => w.year === week.year && w.week === week.week);

                    return (
                      <TableCell
                        key={`${week.year}-${week.week}`}
                        align="center"
                        onClick={() => handleProductClick(productData.product)}
                      >
                        <Stack direction="row" justifyContent="center" alignItems="center" gap={1}>

                          {getDateFromYearAndWeek(week?.year, week?.week).startDate <= new Date() && (
                            <Typography className="sales" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {weekData?.sales.quantity || 0}

                              {weekData?.sales.anomaly && (
                                <Warning
                                  sx={{
                                    color: 'warning.main',
                                    fontSize: '16px',
                                    ml: 0.5,
                                    verticalAlign: 'middle'
                                  }}
                                />
                              )}
                            </Typography>
                          )}


                          {(week.week === getISOWeek(new Date()) && week.year === getISOWeekYear(new Date())) && (
                            <Typography >/</Typography>
                          )}

                          {(weekData?.forecast?.quantity !== undefined)
                            && getDateFromYearAndWeek(week?.year, week?.week).endDate > new Date() && (
                              <Typography color="primary">
                                {weekData.forecast.quantity}
                              </Typography>
                            )}

                        </Stack>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>


        {isLoading && (
          <Stack p={4} direction="row" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Stack>
        )}

        <Stack p={2} direction="row" justifyContent="center" alignItems="center">
          <Pagination
            count={sales?.status === 'success' ? sales.totalPages : 0}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="large"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Stack>

      </Stack>
    )
  );
};

export default SalesTable; 